import React, { useRef, useEffect, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import { Printer, Download } from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { getDeliveryOrderDetails } from "@/redux/ImportBooking/actions";
import { getMasterCompanyDetails } from "@/redux/Common/actions";
import { showToast } from "@/redux/Toast/actions";

/** Matches legacy PDF: "6 MAY 2026" */
const formatDoDate = (dateString) => {
    if (!dateString) return "";
    return moment(dateString).isValid()
        ? moment(dateString).format("DD MMM YYYY").toUpperCase()
        : "";
};

const getAllStyles = () => {
    let styles = "";

    for (let i = 0; i < document.styleSheets.length; i++) {
        const sheet = document.styleSheets[i];
        try {
            const rules = sheet.cssRules;
            for (let j = 0; j < rules.length; j++) {
                styles += rules[j].cssText;
            }
        } catch (err) {
            // Ignore CORS stylesheets
        }
    }

    return styles;
};


const DeliveryOrderPrint = () => {
    const location = useLocation();
    const dispatch = useDispatch();
    const {
        countryList,
        documentTypePurchaseList,
        documentTypeSalesList,
        incoTermsList,
        quantityTypeList,
        salesPersonList,
        uomTypeList,
        commodityGroupList,
        containerTypeList,
        operatorList,
        moveTypeList,
        currency_list,
        charges_basis_list,
        MasterCompanyDetails: reduxCompanyDetails,
    } = useSelector((state) => state.common);


    const { id: encodedData } = useParams();

    const queryParams = new URLSearchParams(location.search);

    const decode = (val) => {
        try {
            return val ? atob(val) : null;
        } catch {
            return null;
        }
    };

    const bookingid = decode(encodedData);


    const [formData, setFormData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [downloading, setDownloading] = useState(false);

    const printRef = useRef();
    const authData = localStorage.getItem("authUser");

    let authUser = {};

    try {
        authUser = JSON.parse(authData || "[{}]")[0];
    } catch {
        authUser = {};
    }

    const storedCompanyDetails = JSON.parse(
        localStorage.getItem("MasterCompanyDetails") || "[]"
    );

    const MasterCompanyDetails =
        reduxCompanyDetails?.length > 0
            ? reduxCompanyDetails
            : storedCompanyDetails;


    const loadCommonData = async () => {
        await dispatch(getMasterCompanyDetails(authUser?.companyId));
        await _getBooking();
    };

    const _getBooking = async () => {
        try {
            const response = await dispatch(getDeliveryOrderDetails(bookingid, authUser?.companyId))

            if (response) {
                setFormData(response);
            } else {
                console.warn("Unexpected API response format:", response);
            }
        } catch (error) {
            console.error("Error fetching booking:", error);
        }
    };

    useEffect(() => {
        const init = async () => {
            setLoading(true);
            try {
                if (bookingid && bookingid !== "0") {
                    await loadCommonData();
                }
            } catch (error) {
                console.error("Error initializing booking:", error);
                dispatch(showToast("Failed to load Delivery order details", "error"));
            } finally {
                setLoading(false);
            }
        };

        init();
    }, []);

    const handlePrint = () => {
        if (!printRef.current) return;

        const printContent = printRef.current.innerHTML;
        const styles = getAllStyles();

        const iframe = document.createElement("iframe");
        iframe.style.position = "fixed";
        iframe.style.right = "0";
        iframe.style.bottom = "0";
        iframe.style.width = "0";
        iframe.style.height = "0";
        iframe.style.border = "0";

        document.body.appendChild(iframe);

        const doc = iframe.contentDocument || iframe.contentWindow.document;

        const html = `
    <!DOCTYPE html>
    <html>
    <head>
        <title>Print</title>
        <meta charset="UTF-8" />
        <style>
            ${styles}

            @page {
                size: A4;
                margin: 10mm;
            }

            html, body {
                height: 100%;
            }

            body {
                 margin: 0;
                    padding: 0;
                    display: flex;
                    flex-direction: column;
                    -webkit-print-color-adjust: exact;
                    print-color-adjust: exact;
            }

            * {
                -webkit-print-color-adjust: exact !important;
                print-color-adjust: exact !important;
            }

            .a4-page {
                    width: 210mm;
                    height: 297mm;
                    display: flex;
                    flex-direction: column;
                }

              .content {
                    flex: 1;
                }

             .footer-fix {
                    margin-top: auto;
                }
        </style>
    </head>
    <body>
        ${printContent}
    </body>
    </html>
    `;

        doc.open();
        doc.write(html);
        doc.close();

        // ✅ Wait for DOM + styles to render properly
        const waitForRender = () =>
            new Promise((resolve) => setTimeout(resolve, 300));

        waitForRender().then(() => {
            iframe.contentWindow.focus();
            iframe.contentWindow.print();

            iframe.contentWindow.onafterprint = () => {
                document.body.removeChild(iframe);
            };
        });
    };

    const handleDownload = async () => {
        if (!printRef.current) return;

        try {
            setDownloading(true);

            const canvas = await html2canvas(printRef.current, {
                scale: 2,
                useCORS: true,
                logging: false,
                backgroundColor: "#ffffff",
            });

            const imgData = canvas.toDataURL("image/png");

            const pdf = new jsPDF({
                orientation: "portrait",
                unit: "mm",
                format: "a4",
            });

            const pageWidth = pdf.internal.pageSize.getWidth();
            const pageHeight = pdf.internal.pageSize.getHeight();

            const imgWidth = canvas.width;
            const imgHeight = canvas.height;

            const ratio = imgWidth / imgHeight;

            let width = pageWidth - 20;
            let height = width / ratio;

            if (height > pageHeight - 20) {
                height = pageHeight - 20;
                width = height * ratio;
            }

            const x = (pageWidth - width) / 2;
            const y = (pageHeight - height) / 2;

            pdf.addImage(imgData, "PNG", x, y, width, height);

            pdf.save(
                `Delivery_Order_${formData?.OurRefNo || formData?.fileNumber || "Document"}_${moment().format("YYYYMMDDHHmmss")}.pdf`
            );

        } catch (error) {
            console.error("Error generating PDF:", error);

            alert("Failed to generate PDF. Please try printing instead.");

        } finally {
            setDownloading(false);
        }
    };


    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-screen bg-gray-100">
                <div className="text-center">
                    <div className="w-12 h-12 border-4 border-blue-900 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                    <p className="text-gray-600">Loading booking data...</p>
                </div>
            </div>
        );
    }


    return (
        <div className="min-h-screen bg-gray-200 print:bg-white text-gray-900">

            {/* ACTION BAR */}
            <div className="print:hidden bg-white shadow-sm border-b border-gray-300 sticky top-0 z-10">
                <div className="max-w-4xl mx-auto px-4 py-3 flex justify-center gap-3">
                    <button
                        onClick={handleDownload}
                        disabled={downloading}
                        className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg border"
                    >
                        <Download className="w-4 h-4" />
                        {downloading ? "Generating PDF..." : "Download PDF"}
                    </button>

                    <button
                        onClick={handlePrint}
                        className="flex items-center gap-2 px-5 py-2 bg-[#1a2744] text-white rounded-lg"
                    >
                        <Printer className="w-4 h-4" />
                        Print
                    </button>
                </div>
            </div>

            {/* A4 CONTAINER */}
            <div className="flex justify-center py-6 print:py-0">
                <div
                    ref={printRef}
                    className="a4-page bg-white shadow-xl print:shadow-none flex flex-col"
                >

                    {/* HEADER — matches Delivery_Order_* PDF */}
                    <div className="relative px-6 pt-6 pb-3 flex justify-between items-start gap-4">

                        {/* LOGO + Powered by (same vertical stack as sample PDF) */}
                        <div className="flex flex-col gap-1 min-w-0 shrink-0">
                            <img
                                src={MasterCompanyDetails?.[0]?.CompanyLogoDark}
                                alt=""
                                className="h-12 object-contain object-left"
                            />
                            {/* <p className="text-[9px] text-gray-600 leading-tight">
                                Powered by www.infox.com
                            </p> */}
                        </div>

                        {/* TITLE */}
                        <div className="absolute left-1/2 -translate-x-1/2 top-11 text-center">
                            <h1 className="text-lg font-extrabold tracking-wide">DELIVERY ORDER</h1>
                        </div>

                        {/* COMPANY ADDRESS */}
                        <div className="text-right text-[10px] font-bold max-w-[200px] leading-snug whitespace-pre-line">
                            {MasterCompanyDetails?.[0]?.ProfileAddressPrint}
                        </div>
                    </div>

                    {/* CONTENT */}
                    <div className="flex-1 px-6 text-[11px] flex flex-col">

                        {/* TOP GRID */}
                        <div>
                            <div className="grid grid-cols-12 border border-black border-b-0 text-xs">

                                {/* LEFT */}
                                <div className="col-span-6 flex flex-col border-r border-black">
                                    <div className="p-2 border-b border-black font-bold leading-tight">
                                        LOCAL DELIVERY OR TRANSFER BY(D/O ISSUED TO):
                                    </div>
                                    <div className="p-2 flex-1 whitespace-pre-line">
                                        {formData?.TruckerAddress || '\u00A0'}
                                    </div>
                                </div>

                                {/* MIDDLE */}
                                <div className="col-span-3 flex flex-col border-r border-black leading-tight">
                                    <div className="p-2 border-b border-black font-bold">
                                        FILE REF NO.:
                                    </div>
                                    <div className="p-2 flex-1 whitespace-pre-line">
                                        {formData?.OurRefNo || '\u00A0'}
                                    </div>
                                </div>

                                {/* RIGHT */}
                                <div className="col-span-3 flex flex-col">
                                    <div className="p-2 border-b border-black font-bold leading-tight">
                                        DATE :
                                    </div>
                                    <div className="p-2 flex-1">
                                        {formatDoDate(formData?.DeliveryDate) || '\u00A0'}
                                    </div>
                                </div>

                            </div>


                            <div className="border border-black text-[11px] break-inside-avoid">

                                {/* ROW 1 */}
                                <div className="grid grid-cols-12 border-b border-black text-xs">

                                    <div className="col-span-3 p-2 border-r border-black flex flex-col">
                                        <b>M B/L NO.</b>
                                        <span>{formData?.MAWBNumber || '\u00A0'}</span>
                                    </div>

                                    <div className="col-span-3 p-2 border-r border-black flex flex-col">
                                        <b>H B/L NO.</b>
                                        <span>{formData?.HAWBNumber || '\u00A0'}</span>
                                    </div>

                                    <div className="col-span-3 p-2 border-r border-black flex flex-col">
                                        <b>ENTRY NO.</b>
                                        <span>{formData?.EntryNo || '\u00A0'}</span>
                                    </div>

                                    <div className="col-span-3 p-2 flex flex-col">
                                        <b>CUST REF NO.</b>
                                        <span>{formData?.CustRefNo || '\u00A0'}</span>
                                    </div>

                                </div>

                                {/* ROW 2 */}
                                <div className="grid grid-cols-12 border-b border-black text-xs">

                                    <div className="col-span-6 p-2 border-r border-black flex flex-col">
                                        <b>ARRIVAL DATE</b>
                                        <span>{formatDoDate(formData?.ArrivalDate) || '\u00A0'}</span>
                                    </div>

                                    <div className="col-span-6 p-2 flex flex-col">
                                        <b>LOCAL DELIVERY OR TRANSFER BY(D/O ISSUED TO):</b>
                                        <span>{formData?.Trucker || '\u00A0'}</span>
                                    </div>

                                </div>

                                {/* ROW 3 */}

                                <div className="grid grid-cols-12 border-b border-black text-xs">

                                    <div className="col-span-3 p-2 border-r border-black flex flex-col">
                                        <b>IMPORTING CARRIER</b>
                                        <span>
                                            {[formData?.CarrierName, formData?.CarrierScac].filter(Boolean).join(" ").trim() || '\u00A0'}
                                        </span>
                                    </div>

                                    <div className="col-span-6 p-2 border-r border-black flex flex-col">
                                        <b>ROUTE</b>
                                        <span>{formData?.RouteDetails || '\u00A0'}</span>
                                    </div>

                                    <div className="col-span-3 p-2 flex flex-col">
                                        <b>POL/POD</b>
                                        <span>
                                            {(() => {
                                                const pc = formData?.LoadPortCode;
                                                const dc = formData?.DestinationToCode;
                                                if (pc && dc) return `${pc} / ${dc}`;
                                                const left = pc || formData?.LoadPort;
                                                const right = dc || formData?.DestinationTo;
                                                const line = [left, right].filter(Boolean).join(" / ");
                                                return line || '\u00A0';
                                            })()}
                                        </span>
                                    </div>

                                </div>



                                {/* ROW 4 — full width like PDF */}
                                <div className="grid grid-cols-12 border-b border-black text-xs">
                                    <div className="col-span-12 p-2 flex flex-col whitespace-pre-line">
                                        <b>CARGO DESCRIPTION</b>
                                        <span>{formData?.CargoDescription || '\u00A0'}</span>
                                    </div>
                                </div>
                                {/* ROW 5 */}

                                <div className="grid grid-cols-12 text-xs">

                                    <div className="col-span-6 p-2 border-r border-black flex flex-col">
                                        <b>CARGO LOCATION</b>
                                        <span className="whitespace-pre-line">{formData?.FreightLocationAddress || '\u00A0'}</span>
                                    </div>

                                    <div className="col-span-6 p-2 flex flex-col">
                                        <b>FOR DELIVER TO</b>
                                        <span className="whitespace-pre-line">{formData?.DeliveryToAddress || '\u00A0'}</span>
                                    </div>

                                </div>
                            </div>
                        </div>
                        <div className="flex flex-col flex-1">

                            <div className="mt-2 text-[11px]">
                                <b>DELIVERY INSTRUCTIONS:-</b> {formData?.DeliveryInstructions || '\u00A0'}
                            </div>

                            <div className="border-t border-black text-xs mt-auto mb-2">

                                {/* Section titles — PDF banner row */}
                                <div className="grid grid-cols-12 border-x border-t border-black">
                                    <div className="col-span-6 p-1 font-bold border-r border-black">
                                        ORIGINAL DELIVERY ORDER
                                    </div>
                                    <div className="col-span-6 p-1 font-bold">
                                        RECEIVED IN GOOD ORDER
                                    </div>
                                </div>

                                {/* Content Row */}
                                <div className="grid grid-cols-12 border border-black border-t-0">

                                    {/* LEFT SIDE */}
                                    <div className="col-span-6 border-r border-black">

                                        {/* Inland Freight Row */}
                                        <div className="grid grid-cols-12 border-b border-black">
                                            <div className="col-span-4 p-1 border-r border-black font-bold">
                                                INLAND FREIGHT
                                            </div>
                                            <div className="col-span-8 p-1 font-bold">
                                                COLLECT
                                            </div>
                                        </div>

                                        {/* Address */}
                                        <div className="p-2 whitespace-pre-line min-h-[4.5rem]">
                                            {formData?.BillToPatyAddress || '\u00A0'}
                                        </div>

                                    </div>

                                    {/* RIGHT SIDE */}
                                    <div className="col-span-6 p-2">

                                        <div className="flex items-center mt-2 mb-2">
                                            <span className="w-[7.5rem] shrink-0 font-bold">SIGNATURE :</span>
                                            <div className="flex-1 border-b border-black min-h-[1.1rem]" />
                                        </div>

                                        <div className="flex items-center mb-2">
                                            <span className="w-[7.5rem] shrink-0 font-bold">PRINT NAME :</span>
                                            <div className="flex-1 border-b border-black min-h-[1.1rem]" />
                                        </div>

                                        <div className="flex items-center">
                                            <span className="w-[7.5rem] shrink-0 font-bold">DATE :</span>
                                            <div className="flex-1 border-b border-black min-h-[1.1rem]" />
                                        </div>

                                    </div>

                                </div>

                            </div>
                            <div className="powered-by-footer text-xs">
                                Powered by {MasterCompanyDetails?.[0]?.PoweredBy}
                            </div>
                        </div>

                    </div>

                </div>
            </div>

            {/* PRINT STYLES */}
            <style>{`
      .a4-page {
          width: 210mm;
          height: 297mm;
          box-sizing: border-box;
        }
 
        @media screen {
          .a4-page { margin: 0 auto; }
        }
 
        @media print {
          @page { size: A4; margin: 10mm; }
 
          html, body {
            width: 210mm;
            height: 297mm;
          }
 
          body {
            margin: 0;
            padding: 0;
            print-color-adjust: exact;
            -webkit-print-color-adjust: exact;
          }
 
          .a4-page {
            width: 100%;
            min-height: auto;
            padding: 0;
            margin: 0;
            box-shadow: none !important;
          }
 
          .print\\:hidden { display: none !important; }
          .print\\:py-0 { padding-top: 0 !important; padding-bottom: 0 !important; }
          .print\\:shadow-none { box-shadow: none !important; }
          .print\\:bg-white { background: white !important; }
        }
    `}</style>
        </div>
    );

};

export default DeliveryOrderPrint;


// const handlePrint = () => {
//     const printContent = printRef.current.innerHTML;

//     // Create hidden iframe
//     const iframe = document.createElement("iframe");
//     iframe.style.position = "fixed";
//     iframe.style.right = "0";
//     iframe.style.bottom = "0";
//     iframe.style.width = "0";
//     iframe.style.height = "0";
//     iframe.style.border = "0";

//     document.body.appendChild(iframe);

//     const iframeDoc = iframe.contentWindow.document;

//     iframeDoc.open();
//     iframeDoc.write(`
//        <!DOCTYPE html>
//        <html>
//        <head>
//            <title>Booking - ${formData?.fileNumber || "Booking"}</title>
//            <meta charset="UTF-8" />
//            <meta name="viewport" content="width=device-width, initial-scale=1.0" />
//            <script src="https://cdn.tailwindcss.com"></script>

//            <style>
//                @page {
//                    size: A4;
//                    margin: 10mm;
//                }

//                html, body {
//                    height: 100%;
//                }

//                body {
//                    margin: 0;
//                    padding: 0;
//                    display: flex;
//                    flex-direction: column;
//                    -webkit-print-color-adjust: exact;
//                    print-color-adjust: exact;
//                }

//                /* A4 container */
//                .a4-page {
//                    width: 210mm;
//                    height: 297mm;
//                    display: flex;
//                    flex-direction: column;
//                }

//                /* Content should grow */
//                .content {
//                    flex: 1;
//                }

//                /* Footer sticks to bottom */
//                .footer-fix {
//                    margin-top: auto;
//                }
//            </style>
//        </head>

//        <body>
//            <div class="a4-page">
//                ${printContent}
//            </div>
//        </body>
//        </html>
//        `);

//     iframeDoc.close();

//     // Print after content loads
//     iframe.onload = () => {
//         iframe.contentWindow.focus();
//         iframe.contentWindow.print();

//         // Cleanup after print
//         iframe.contentWindow.onafterprint = () => {
//             document.body.removeChild(iframe);
//         };
//     };
// };
